import React, { useState, useEffect } from 'react';
import './register.css'; 
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Popup from './Popup/Popup';
import Newheader from '../NewHeader/Newheader';
import Newfooter from '../NewFooter/Newfooter';
import { 
  VIPSDataServiceURL, 
  requestOptions 
} from '../../Constants/GlobalConstant';

import { GetCountries } from '../../CommonFuntion/SchemaAPICalls';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// --- CONFIGURATION ---
const CONFIG = {
  REDIRECT_TIMEOUT: 2000,
  API_PATH: '/user/register',
  MAX_LENGTH_DEFAULT: 100,
  MAX_LENGTH_SHORT: 30
};

const TEXTS = {
    title: "Register with Avery Dennison",
    registerBtn: "Register",
    registering: "Registering...",
    labelName: "Name",
    labelUserId: "User ID",
    labelPass: "Password",
    labelConfirm: "Confirm Password",
    labelEmail: "Email",
    labelCompany: "Company",
    labelPhone: "Phone Number",
    labelAddr1: "Address Line 1",
    labelAddr2: "Address Line 2",
    labelPost: "Postal Code",
    labelCountry: "Country",
    labelState: "County/Region",
    labelCity: "City",
    labelQuestion: "Security Question",
    labelAnswer: "Answer",
    selectOption: "--select--",
    showPass: "Show Password",
    hidePass: "Hide Password",
    weak: "Weak", fair: "Fair", strong: "Strong", perfect: "Perfect",
    ruleUpper: "At least one uppercase letter",
    ruleNum: "At least one number",
    ruleSpecial: "At least one special character",
    req: "is required",
    invUser: "Invalid Username",
    invUserId: "Invalid User ID / Email",
    invEmail: "Email is invalid",
    invPhone: "Invalid characters in phone number",
    invCompany: "@ Symbol is not allowed",
    passMatch: "Passwords do not match",
    passPerfect: "Password does not meet complexity requirements.",
    success: "Registration successful! Redirecting to login...",
    fail: "Registration failed"
};

// --- HELPER TO DECODE HTML ENTITIES ---
const decodeHtmlEntities = (str) => {
    if (!str) return '';
    const txt = document.createElement("textarea");
    txt.innerHTML = str;
    return txt.value;
};

const Register = () => {
  const navigate = useNavigate();
  const t = TEXTS; 

  const initialFormData = {
    username: '', userid: '', Password: '', ConfirmPassword: '', Email: '',
    Company: '', PhoneNumber: '', Address1: '', Address2: '', country: '',
    State: '', City: '', PostCode: '', Question: '', Answer: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const [error, setError] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
  // --- STATE FOR DYNAMIC VALIDATION ---
  const [minPassLength, setMinPassLength] = useState(8); 
  const [passComplexityRegex, setPassComplexityRegex] = useState(null);
  const [emailComplexityRegex, setEmailComplexityRegex] = useState(null);

  const [countryList, setCountryList] = useState([]); 
    
  const [strength, setStrength] = useState({ level: '', width: '0%', className: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordRules, setPasswordRules] = useState({
    length: false, uppercase: false, number: false, special: false
  });
  const [focusedField, setFocusedField] = useState('');
  const [popup, setPopup] = useState({ show: false, message: '', type: 'error' });

  // --- API CALL: System Configuration ---
  useEffect(() => {
    const fetchParameter = async (paramName) => {
        try {
            const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
            
            // POST request pattern for system parameters
            const response = await axios.post(
                `${cleanUrl}/Misc/GetSystemParameterValueByName`, 
                {}, 
                { 
                    params: { name: paramName }, 
                    headers: { 
                        ...requestOptions.headers, 
                        'Content-Type': 'application/json' 
                    } 
                }
            );

            if (response.data) {
                // Clean data (remove quotes/newlines)
                return String(response.data)
                    .replace(/^"|"$/g, '')          
                    .replace(/(\r\n|\n|\r)/gm, "")  
                    .trim();
            }
            return null;
        } catch (err) {
            console.warn(`[Config] Error fetching ${paramName}:`, err);
            return null;
        }
    };

    const loadConfig = async () => {
        const len = await fetchParameter('MinimumPasswordLength');
        if (len) setMinPassLength(Number(len));

        const passRegex = await fetchParameter('PasswordComplexity');
        if (passRegex) {
            // FIX: Decode regex here
            setPassComplexityRegex(decodeHtmlEntities(passRegex));
        }

        const emailRegex = await fetchParameter('EmailComplexity');
        if (emailRegex) {
            // FIX: Decode regex here too
            setEmailComplexityRegex(decodeHtmlEntities(emailRegex));
        }
    };

    loadConfig();
  }, []);

  // --- API CALL: Countries ---
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const result = await GetCountries();
        let countriesArray = [];
        if (Array.isArray(result)) {
            countriesArray = result;
        } else if (result && Array.isArray(result.d)) {
            countriesArray = result.d;
        } else if (result && Array.isArray(result.data)) {
            countriesArray = result.data;
        }

        if (countriesArray.length > 0) {
            setCountryList(countriesArray);
        }
      } catch (error) {
        // Handle error silently
      }
    };
    fetchCountries();
  }, []);
    
  const closePopup = () => setPopup({ show: false, message: '', type: '' });
  const handlePasswordToggle = () => setShowPassword(!showPassword);
  const handleConfirmPasswordToggle = () => setShowConfirmPassword(!showConfirmPassword);

  // --- Helper: Dynamic Email Validation ---
  const validateEmail = (emailValue) => {
      const strictFallback = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
      
      if (emailComplexityRegex) {
          try {
              // Now using the decoded regex
              const regex = new RegExp(emailComplexityRegex);
              return regex.test(emailValue);
          } catch (e) {
              return strictFallback.test(emailValue);
          }
      }
      return strictFallback.test(emailValue);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let errorMsg = '';

    switch (name) {
      case 'username':
        if (!value.trim()) errorMsg = `${t.labelName} ${t.req}`;
        else if (/[^A-Za-z0-9_@\s]/.test(value)) errorMsg = t.invUser; 
        break;

      case 'userid':
        if (!value.trim()) {
            errorMsg = `${t.labelUserId} ${t.req}`;
        } 
        else if (value.includes('@')) {
            if (!validateEmail(value)) {
                errorMsg = t.invUserId;
            }
        } 
        else {
            if (/[^A-Za-z0-9_@.\-\s]/.test(value)) {
                errorMsg = t.invUserId;
            }
        }
        break;

      case 'Password':
        if (!value.trim()) errorMsg = `${t.labelPass} ${t.req}`;
        checkStrength(value);
        break;
      case 'ConfirmPassword':
        if (!value.trim()) errorMsg = `${t.labelConfirm} ${t.req}`;
        else if (formData.Password !== value) errorMsg = t.passMatch;
        break;
      
      case 'Email':
        if (!value.trim()) {
            errorMsg = `${t.labelEmail} ${t.req}`;
        } else {
            const isValid = validateEmail(value);
            if (!isValid) errorMsg = t.invEmail;
        }
        break;

      case 'PhoneNumber':
        if (value.trim() && !/^[0-9()+-\s]+$/.test(value)) errorMsg = t.invPhone;
        break;
      case 'Company':
        if (!value.trim()) errorMsg = `${t.labelCompany} ${t.req}`;
        else if (value.includes('@')) errorMsg = t.invCompany;
        break;
      case 'Address1':
      case 'PostCode':
      case 'Question':
      case 'Answer':
      case 'City':
      case 'State':
      case 'country':
        if (!value.trim()) {
           const labelMap = {
               Address1: t.labelAddr1, PostCode: t.labelPost, Question: t.labelQuestion, 
               Answer: t.labelAnswer, City: t.labelCity, State: t.labelState, country: t.labelCountry
           };
           errorMsg = `${labelMap[name]} ${t.req}`;
        }
        break;
      default: break;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: errorMsg }));
  };

  // --- UPDATED PASSWORD STRENGTH LOGIC ---
  const checkStrength = (password) => {
    // 1. Basic Rules
    const lengthValid = password.length >= minPassLength;
    const uppercaseValid = /[A-Z]/.test(password);
    const numberValid = /[0-9]/.test(password);
    const specialValid = /[^A-Za-z0-9]/.test(password);

    // 2. Server Regex Check (Using Decoded Regex)
    let matchesServerRegex = true;
    if (passComplexityRegex) {
        try {
            const regex = new RegExp(passComplexityRegex);
            matchesServerRegex = regex.test(password);
        } catch (e) { matchesServerRegex = false; }
    }

    // 3. Update Visual Checklist
    const rules = {
      length: lengthValid,
      uppercase: uppercaseValid,
      number: numberValid,
      special: specialValid
    };
    setPasswordRules(rules);

    // 4. Determine Level
    if (!password) {
        setStrength({ level: '', width: '0%', className: '' });
        return;
    }

    if (!lengthValid) {
        setStrength({ level: t.weak, width: '25%', className: 'strength-weak' });
        return;
    }

    const basicCount = [uppercaseValid, numberValid].filter(Boolean).length;

    if (basicCount === 2 && specialValid && matchesServerRegex) {
        setStrength({ level: t.perfect, width: '100%', className: 'strength-perfect' }); 
    } else if (basicCount === 2) {
        setStrength({ level: t.strong, width: '75%', className: 'strength-strong' });
    } else if (basicCount === 1) {
        setStrength({ level: t.fair, width: '50%', className: 'strength-fair' });
    } else {
        setStrength({ level: t.weak, width: '25%', className: 'strength-weak' });
    }
  };

  const handleCreate = async () => {
    setError({});
    const newErrors = {};

    const validationChecks = [
      { name: 'username', label: t.labelName },
      { name: 'userid', label: t.labelUserId },
      { name: 'Password', label: t.labelPass },
      { name: 'ConfirmPassword', label: t.labelConfirm },
      { name: 'Email', label: t.labelEmail },
      { name: 'Company', label: t.labelCompany },
      { name: 'Address1', label: t.labelAddr1 },
      { name: 'PostCode', label: t.labelPost },
      { name: 'country', label: t.labelCountry },
      { name: 'State', label: t.labelState },
      { name: 'City', label: t.labelCity },
      { name: 'Question', label: t.labelQuestion },
      { name: 'Answer', label: t.labelAnswer },
    ];

    for (const check of validationChecks) {
      const value = formData[check.name];
      if (!value || (typeof value === 'string' && !value.trim())) {
        const errorMsg = `${check.label} ${t.req}`;
        setError((prev) => ({ ...prev, [check.name]: errorMsg }));
        setPopup({ show: true, message: errorMsg, type: 'error' });
        return; 
      }
    }

    if (formData.userid.includes('@') && !validateEmail(formData.userid)) {
        newErrors.userid = t.invUserId;
    }

    if (!validateEmail(formData.Email)) {
        newErrors.Email = t.invEmail;
    }

    if (formData.Company.includes('@')) {
      newErrors.Company = t.invCompany;
    }
    if (formData.PhoneNumber.trim() && !/^[0-9()+-\s]+$/.test(formData.PhoneNumber)) {
      newErrors.PhoneNumber = t.invPhone;
    }
    if (formData.Password !== formData.ConfirmPassword) {
      newErrors.ConfirmPassword = t.passMatch;
    }
    
    // Validate Strength (Must be Perfect/Green if server regex exists)
    // If you want to force "Perfect" whenever a regex exists:
    if (passComplexityRegex) {
         try {
             const regex = new RegExp(passComplexityRegex);
             if (!regex.test(formData.Password)) {
                 newErrors.Password = t.passPerfect;
             }
         } catch(e) { }
    } else {
         // Fallback if no server regex, ensure at least strong
         if (strength.className !== 'strength-perfect' && strength.className !== 'strength-strong') {
             newErrors.Password = t.passPerfect;
         }
    }

    if (Object.keys(newErrors).length > 0) {
        setError(newErrors);
        setPopup({ show: true, message: Object.values(newErrors)[0], type: 'error' });
        return;
    }

    setIsSubmitting(true);

    try {
      const apiPayload = {
        "User_Id": formData.userid,
        "User_Name": formData.username,
        "Password": formData.Password,
        "Email": formData.Email,
        "Phone": formData.PhoneNumber,
        "Company": formData.Company,
        "Address1": formData.Address1,
        "Address2": formData.Address2,
        "Address3": formData.City,
        "Address4": formData.State,
        "Postcode": formData.PostCode,
        "Country_Code": formData.country, 
        "SecretQuestion": formData.Question,
        "SecretAnswer": formData.Answer
      };
        
      const url = `${VIPSDataServiceURL}${CONFIG.API_PATH}`; 
      
      const response = await axios.post(url, apiPayload, { 
        headers: requestOptions.headers 
      });

      if (response.status === 200 || response.status === 201) {
        setPopup({ show: true, message: t.success, type: 'success' });
        setFormData(initialFormData); 
        setStrength({ level: '', width: '0%', className: '' });
        setPasswordRules({ length: false, uppercase: false, number: false, special: false });
        setError({});

        setTimeout(() => {
            navigate('/');
        }, CONFIG.REDIRECT_TIMEOUT);

      } else {
        setPopup({ show: true, message: `${t.success} Status: ${response.status}`, type: 'error' });
      }
        
    } catch (apiError) {
      const message = apiError.response?.data?.message || 'A server error occurred.';
      setPopup({ show: true, message: `${t.fail}: ${message}`, type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fields = [
    { label: t.labelName, name: 'username', type: 'text', limit: CONFIG.MAX_LENGTH_DEFAULT },
    { label: t.labelUserId, name: 'userid', type: 'text', limit: CONFIG.MAX_LENGTH_DEFAULT },
    { label: t.labelPass, name: 'Password', type: showPassword ? 'text' : 'password', toggle: handlePasswordToggle, show: showPassword, limit: CONFIG.MAX_LENGTH_DEFAULT },
    { label: t.labelConfirm, name: 'ConfirmPassword', type: showConfirmPassword ? 'text' : 'password', toggle: handleConfirmPasswordToggle, show: showConfirmPassword, limit: CONFIG.MAX_LENGTH_DEFAULT },
    { label: t.labelEmail, name: 'Email', type: 'email', limit: CONFIG.MAX_LENGTH_DEFAULT },
    { label: t.labelCompany, name: 'Company', type: 'text', limit: CONFIG.MAX_LENGTH_DEFAULT },
    { label: t.labelPhone, name: 'PhoneNumber', type: 'text', limit: CONFIG.MAX_LENGTH_DEFAULT },
    { label: t.labelAddr1, name: 'Address1', type: 'text', limit: CONFIG.MAX_LENGTH_DEFAULT },
    { label: t.labelAddr2, name: 'Address2', type: 'text', limit: CONFIG.MAX_LENGTH_DEFAULT },
    { label: t.labelPost, name: 'PostCode', type: 'text', limit: CONFIG.MAX_LENGTH_DEFAULT }
  ];
   
  return (
    <div id="register-iso-scope">
      <div className="register-page-container">
        <Newheader/>
        <div className="form-wrapper">
          {popup.show && (
            <Popup
              message={popup.message}
              type={popup.type}
              onClose={closePopup}
            />
          )}
        
          <div className="form-card" style={{ position: 'relative' }}>
            
            <span className="form-title">{t.title}</span>
            <form>
              {fields.map((field) => (
                <div className="form-group" key={field.name}>
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    onFocus={() => setFocusedField(field.name)}
                    onBlur={() => setFocusedField('')}
                    placeholder=" "
                    required={field.name !== 'Address2' && field.name !== 'PhoneNumber'}
                    className={error[field.name] ? 'input-error' : ''}
                    maxLength={field.limit} 
                  />
                  <label>
                    {field.label} 
                    {field.name !== 'Address2' && field.name !== 'PhoneNumber' && (
                      <span className="required"></span>
                    )}
                  </label>
                  
                  {field.toggle && focusedField !== field.name && (
                    <span 
                      className="toggle-icon" 
                      onClick={field.toggle} 
                      title={field.show ? t.hidePass : t.showPass}
                    >
                      {field.show ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                    </span>
                  )}

                  {field.name === 'Password' && focusedField === 'Password' && (
                    <>
                      <div className="strength-meter">
                        <div className={`strength-bar ${strength.className}`} style={{ width: strength.width }}></div>
                      </div>
                      <div className="strength-text">{strength.level}</div>
                      <ul className="password-rules">
                        <li className={passwordRules.length ? 'valid' : ''}>
                          Minimum {minPassLength !== null ? minPassLength : '...'} characters
                        </li>
                        <li className={passwordRules.uppercase ? 'valid' : ''}>{t.ruleUpper}</li>
                        <li className={passwordRules.number ? 'valid' : ''}>{t.ruleNum}</li>
                        <li className={passwordRules.special ? 'valid' : ''}>{t.ruleSpecial}</li>
                      </ul>
                    </>
                  )}
                  {error[field.name] && <div className="error-message">{error[field.name]}</div>}
                </div>
              ))}

              <div className="form-group">
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={error.country ? 'input-error' : ''}
                >
                  <option value="">{t.selectOption}</option>
                  {countryList.map((c, index) => (
                    <option key={index} value={c.A2}> 
                      {c.Country ? c.Country.trim() : ''}
                    </option>
                  ))}
                </select>
                <label>{t.labelCountry} <span className="required"></span></label>
                {error.country && <div className="error-message">{error.country}</div>}
              </div>

              <div className="form-group">
                <input
                  type="text"
                  value={formData.State}
                  onChange={handleChange}
                  name="State"
                  placeholder=" "
                  className={error.State ? 'input-error' : ''}
                  maxLength={CONFIG.MAX_LENGTH_SHORT} 
                />
                <label>{t.labelState}<span className="required"></span></label>
                {error.State && <div className="error-message">{error.State}</div>}
              </div>

              <div className="form-group">
                <input
                  type="text"
                  value={formData.City}
                  onChange={handleChange}
                  name="City"
                  placeholder=" "
                  className={error.City ? 'input-error' : ''}
                  maxLength={CONFIG.MAX_LENGTH_SHORT}
                />
                <label>{t.labelCity} <span className="required"></span></label>
                {error.City && <div className="error-message">{error.City}</div>}
              </div>

              <div className="form-group">
                <input
                  type="text"
                  name="Question"
                  value={formData.Question}
                  onChange={handleChange}
                  placeholder=" "
                  required
                  className={error.Question ? 'input-error' : ''}
                  maxLength={CONFIG.MAX_LENGTH_DEFAULT}
                />
                <label>{t.labelQuestion} <span className="required"></span></label>
                {error.Question && <div className="error-message">{error.Question}</div>}
              </div>

              <div className="form-group">
                <input
                  type="text"
                  name="Answer"
                  value={formData.Answer}
                  onChange={handleChange}
                  placeholder=" "
                  required
                  className={error.Answer ? 'input-error' : ''}
                  maxLength={CONFIG.MAX_LENGTH_DEFAULT}
                />
                <label>{t.labelAnswer} <span className="required"></span></label>
                {error.Answer && <div className="error-message">{error.Answer}</div>}
              </div>

              <button type="button" className="form-button" onClick={handleCreate} disabled={isSubmitting}>
                {isSubmitting ? t.registering : t.registerBtn}
              </button>
            </form>
          </div>
        </div>
        <Newfooter/>
      </div>
    </div>
  );
};

export default Register;